// @ts-check
import { createContext, useEffect, useLayoutEffect, useState } from "react";
import request from '@/utils/axios';
import { CAT_ID_MAP } from '@/utils/utils';
import { useAuth0 } from "@auth0/auth0-react";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

const legacyRoot = process.env.LEGACY_ROOT || process.env.API_ROOT;

/**
 * @param {{
 *   sitepageid: number;
 *   pagedata?: import("../components/types").CMSPage;
 *   alias?: string;
 *   mutateBody?: (data: string) => string;
 *   renderOtherType?: boolean;
 *   bypassLogin?: boolean;
 *   bypassAuth?: boolean;
 *   isLoading?: boolean;
 * }} opts
 */
export default function useCMSPageData(opts) {
   const {
    sitepageid,
    pagedata,
    alias,
    mutateBody = (d) => d,
    renderOtherType,
    bypassLogin,
    bypassAuth,
    isLoading,
  } = opts;
  const { loginWithRedirect } = useAuth0();
  const router = useRouter();
  const { role: roles, isLoggedIn, isLoading: authIsLoading } = useSelector(/** @param {{ auth: {role: string[], isLoggedIn: boolean, isLoading: boolean } }} state */(state) => state.auth);
  const _sitepageid = sitepageid || /** @type {string} */(router.query.slug);
  const [loading, setLoading] = useState(!pagedata);
  const [error, setError] = useState(false);
  const [unauthorizedError, setUnauthorizedError] = useState(false);
  const [data, setData] = useState(pagedata || null);

  let canonicalHREF = window.location.href;
  if (data?.PageAlias && data.PageAlias !== _sitepageid) {
    canonicalHREF = `${window.location.origin}/${data.PageAlias}/`;
  }
  let meta = pagedata?.meta || {
    title: data?.MetaTitle ?? '',
    description: data?.MetaDescription ?? '',
  };
  let hero = pagedata?.hero || {
    headline: (data?.HeroHeadline || data?.MetaTitle) ?? '',
    description: data?.HeroParagraph ?? '',
    subTitle: data?.HeroSubtitle ?? '',
    image: {
      id: data?.HeroImageId ?? null,
      alt: data?.HeroImageAltText ?? '',
    },
  };
  let body = mutateBody(data?.body ?? data?.Body ?? '')            
      .replace(/<br><br>/g, "<div class='lineBreak'></div>")
      .replace(/<br><\/p>/g, '</p>')
      .replace(/src="\/pciwebsite/g, `src="${legacyRoot}/pciwebsite`);
  let contentGroupItems = [];
  if (data?.ContentGroupItems && data?.ContentGroupIndex === 'Y') {
    contentGroupItems = data.ContentGroupItems.filter(items => items.ContentItemId !== _sitepageid);
  }

  useLayoutEffect(() => {
    if (Boolean(body) && !loading && window.location.hash) {
      /** @type {HTMLElement} */
      const hash = document.querySelector(`[name=${window.location.hash.replace('#', '')}]`);
      if (!hash) return;
      const hashScroll = hash.offsetTop;
      const offset = window.innerWidth > 992 ? 80 : 72;
      window.scrollTo(0, hashScroll - offset);
    }
  }, [body, loading]);

  useEffect(() => {
    if (_sitepageid === undefined || !(_sitepageid || alias || !roles?.length)) return;
    if (isLoading) return;
    (async function mounted() {
      try {
        if (!bypassAuth) {
          const hasAccess = await hasPageAccess();
          if (!hasAccess) {
            setUnauthorizedError(true);
          }
        }

        if (pagedata) {
          setData(pagedata);
          setLoading(false);
          return;
        }

        const response = await request({
          url: `cms/contentpage`,
          params: alias ? { alias } : { corePageId: _sitepageid },
        });
        setData(response.data);

        if (renderOtherType && response.data.Cat1Id.toString() in CAT_ID_MAP) {
          redirectWithCatId(response.data.Cat1Id, _sitepageid, router);
          return;
        }

        setLoading(false);
      } catch (err) {
        console.log('Something happenned', err)
        setError(true);
        setLoading(false);
      }
    })();
  }, [_sitepageid, alias, roles, isLoggedIn, isLoading, authIsLoading, bypassLogin, pagedata]);

  /** @returns {Promise<boolean>} */
  async function hasPageAccess() {
    /** 
     * @type {import("axios").AxiosResponse<{CorePageId: number; MemberAccessLevels: {
     *   WebAccessCode: string;
     *   WebAccessDescription: string;
     *   WebAccessName: string | null;
     *   Auth0RoleId: number | null;
     * }[]}>} 
     */
    const securityResponse = await request({
      url: 'cms/contentpagesecurity',
      params: {
        corePageId: _sitepageid,
      },
    });
    const { MemberAccessLevels } = securityResponse.data;
    if (MemberAccessLevels?.length !== 0 && !isLoggedIn && !isLoading && !bypassLogin) {
      loginWithRedirect({
        appState: {
          returnTo: window.location.pathname,
        },
      });
      return false;
    }
    if (MemberAccessLevels !== undefined && MemberAccessLevels?.length !== 0) {
      const hasAccessToContent = MemberAccessLevels.some(level =>
        roles.includes(level.WebAccessDescription)
      );
      return hasAccessToContent;
    }
    return true;
  }

  return {
    data,
    body,
    contentGroupItems,
    hero,
    meta,
    canonicalHREF,
    error,
    unauthorizedError,
    loading,
    sitepageid,
  }
}

/** 
 * @param {number | string} catId
 * @param {number | string} sitePageId
 * @param {import("next/router").NextRouter} router
 * @returns {Promise<number | void>} errorCode
 */
async function redirectWithCatId(catId, sitePageId, router) {
  const cat = CAT_ID_MAP[catId];
  const response = await request({
    url: `cms/${cat}`,
    params: { sitePageId },
  });
  if (response.data.SitePageId === 0) {
    return 404
  } else {
    const catRoute = (() => {
      switch (catId.toString()) {
        case '9998':
          return 'media/news-releases/release';
        case '9997':
          return 'events/meetings/meeting';
        case '9996':
          return 'publications';
        default:
          return '';
      }
    })();
    if (catRoute) {
      router.replace(`/${catRoute}/[slug]`, `/${catRoute}/${response.data.SitePageId}`);
      return;
    }
  }
}